import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { swipeAPI, matchAPI, messageAPI } from '../api/endpoints';
import { useAuth } from './AuthContext';

const SwipeContext = createContext();

export const useSwipe = () => {
  const context = useContext(SwipeContext);
  if (!context) {
    throw new Error('useSwipe must be used within a SwipeProvider');
  }
  return context;
};

export const SwipeProvider = ({ children }) => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [messages, setMessages] = useState({});
  const [typingStatus, setTypingStatus] = useState({}); // {matchId: isTyping}
  const [isLoading, setIsLoading] = useState(false);

  const loadProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await swipeAPI.getProfiles(10);
      setProfiles(data.profiles || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load profiles:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMatches = useCallback(async () => {
    try {
      const data = await matchAPI.getMatches();
      setMatches(data.matches || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load matches:', error);
      }
    }
  }, []);

  const loadMessages = useCallback(async (matchUserId) => {
    try {
      const data = await messageAPI.getMessages(matchUserId);
      setMessages(prev => ({
        ...prev,
        [matchUserId]: data.messages || []
      }));
      // Update typing status
      setTypingStatus(prev => ({
        ...prev,
        [matchUserId]: data.is_typing || false
      }));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load messages:', error);
      }
    }
  }, []);

  // Load profiles when user logs in
  useEffect(() => {
    if (user) {
      loadProfiles();
      loadMatches();
    }
  }, [user, loadProfiles, loadMatches]);

  const swipeRight = useCallback(async (profile) => {
    try {
      // Remove profile from stack immediately for better UX
      setProfiles(prev => prev.filter(p => p.id !== profile.id));
      
      // IMPORTANT: Wait for swipe to be recorded before loading more profiles
      const result = await swipeAPI.swipe(profile.id, 'right');
      
      if (result.matched) {
        // Refresh matches list
        await loadMatches();
        return { matched: true, profile: result.match };
      }
      
      // Load more profiles if running low (AFTER swipe is recorded)
      setProfiles(prev => {
        if (prev.length <= 3) {
          // Small delay to ensure backend has processed the swipe
          setTimeout(() => loadProfiles(), 300);
        }
        return prev;
      });
      
      return { matched: false };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Swipe right failed:', error);
      }
      // On error, reload profiles to sync state
      loadProfiles();
      return { matched: false };
    }
  }, [loadMatches, loadProfiles]);

  const swipeLeft = useCallback(async (profile) => {
    try {
      // Remove profile from stack immediately
      setProfiles(prev => prev.filter(p => p.id !== profile.id));
      
      // IMPORTANT: Wait for swipe to be recorded before loading more profiles
      await swipeAPI.swipe(profile.id, 'left');
      
      // Load more profiles if running low (AFTER swipe is recorded)
      setProfiles(prev => {
        if (prev.length <= 3) {
          // Small delay to ensure backend has processed the swipe
          setTimeout(() => loadProfiles(), 300);
        }
        return prev;
      });
      
      return { matched: false };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Swipe left failed:', error);
      }
      // On error, reload profiles to sync state
      loadProfiles();
      return { matched: false };
    }
  }, [loadProfiles]);

  const sendMessage = useCallback(async (matchUserId, text) => {
    try {
      const newMessage = await messageAPI.sendMessage(matchUserId, text);
      
      // Update messages state
      setMessages(prev => ({
        ...prev,
        [matchUserId]: [...(prev[matchUserId] || []), newMessage]
      }));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to send message:', error);
      }
      throw error;
    }
  }, []);

  const setTyping = useCallback(async (matchUserId) => {
    try {
      await messageAPI.setTyping(matchUserId);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to set typing status:', error);
      }
    }
  }, []);
  
  const handleUnmatchNotification = useCallback((unmatchedByUserId) => {
    // Remove the match from the matches list
    setMatches(prevMatches => 
      prevMatches.filter(match => match.id !== unmatchedByUserId)
    );
    
    // Clear messages for that match
    setMessages(prevMessages => {
      const newMessages = { ...prevMessages };
      // Find and delete messages for this user
      Object.keys(newMessages).forEach(matchId => {
        delete newMessages[matchId];
      });
      return newMessages;
    });
    
    // Reload matches to get fresh data
    loadMatches();
  }, [loadMatches]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    profiles,
    matches,
    messages,
    typingStatus,
    isLoading,
    swipeRight,
    swipeLeft,
    sendMessage,
    setTyping,
    loadMessages,
    loadMatches,
    loadProfiles,
    handleUnmatchNotification
  }), [profiles, matches, messages, typingStatus, isLoading, swipeRight, swipeLeft, sendMessage, setTyping, loadMessages, loadMatches, loadProfiles, handleUnmatchNotification]);

  return (
    <SwipeContext.Provider value={contextValue}>
      {children}
    </SwipeContext.Provider>
  );
};
